import { Injectable } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { GridApi } from 'ag-grid-community';
import { debounceTime, map, merge, Observable, takeUntil, tap } from 'rxjs';
import { parseMaskedStringToNumber } from '../../../../../core/functions/number-helpers';
import { FormGroupType } from '../../../../../core/models/form-group-type';
import { RegExPattern } from '../../../../../core/models/regexp-pattern/regex-pattern';
import { OrderLineItemConstants } from '../constants/order-line-item-constants';
import { OrderLineItemFormModel } from '../models/order-line-item-form-model';
import { OrderLineItemRequest } from '../models/order-line-item-request';
import { OrderLineItemResponse } from '../models/order-line-item-response';

@Injectable({
    providedIn: 'root',
})
export class OrderLineItemFormService {
    getFormDefinition(): FormGroup<FormGroupType<OrderLineItemFormModel>> {
        return new FormGroup<FormGroupType<OrderLineItemFormModel>>({
            id: new FormControl<number | null>(null),
            tempId: new FormControl<string | null>(null),
            productId: new FormControl<number | null>(null, [
                Validators.required,
            ]),
            productName: new FormControl<string | null>(null),
            quantity: new FormControl<string | null>(
                OrderLineItemConstants.INITIAL_QUANTITY_VALUE,
                [Validators.required],
            ),
            price: new FormControl<string | null>(null, [Validators.required]),
            description: new FormControl<string | null>(null, [
                Validators.maxLength(
                    OrderLineItemConstants.DESCRIPTION_MAX_LENGTH,
                ),
                Validators.pattern(
                    RegExPattern.PrintableAsciiAndPermittedControlCharactersExceptAngleBracketsAndPipes,
                ),
            ]),
            weight: new FormControl<number | null>(null),
            isDeleted: new FormControl<boolean>(false),
        });
    }

    /**
     * Sets up listeners on the quantity and price form controls to trigger the provided action
     * whenever their values change.
     *
     * @param form The FormGroup containing the quantity and price controls.
     * @param gridApi The GridApi instance to update the grid row data.
     * @param postModificationAction The action to execute after modifying the grid row data.
     * @param until$ The Observable used to unsubscribe from the value changes when it emits.
     * @returns An observable that emits when quantity or price changes. The caller should subscribe to this.
     */
    setupQuantityAndPriceFormControlListeners(
        form: FormGroup<FormGroupType<OrderLineItemFormModel>>,
        gridApi: GridApi | undefined,
        postModificationAction: () => void,
        until$: Observable<void>,
    ): Observable<void> {
        const quantityControl = form.controls.quantity;
        const priceControl = form.controls.price;

        return merge(
            quantityControl.valueChanges,
            priceControl.valueChanges,
        ).pipe(
            takeUntil(until$),
            debounceTime(0), // Ensures that the form control values are updated before we read them
            tap(() => {
                // Updating the grid row data whenever quantity or price changes
                gridApi?.applyTransaction({
                    update: [form.value],
                });
            }),
            tap(() => {
                postModificationAction();
            }),
            map(() => void 0),
        );
    }

    /**
     * Sets up a listener on the `isDeleted` form control to manage enabling/disabling
     * the last non-deleted form group's `isDeleted` control in the form array. There must always be
     * at least one order line item that is not marked as deleted.
     *
     * @param form The FormGroup containing the `isDeleted` control.
     * @param formArray The FormArray containing all order line item forms.
     * @param until$ The Observable used to unsubscribe from the value changes when it emits.
     * @returns An observable that emits when the `isDeleted` control value changes. The caller should subscribe to this.
     */
    setupIsDeletedFormControlListener(
        form: FormGroup<FormGroupType<OrderLineItemFormModel>>,
        formArray: FormArray<FormGroup<FormGroupType<OrderLineItemFormModel>>>,
        until$: Observable<void>,
    ): Observable<void> {
        const isDeletedControl = form.controls.isDeleted;

        return isDeletedControl.valueChanges.pipe(
            takeUntil(until$),
            debounceTime(0), // Ensures that the form control values are updated before we read them
            tap(() => {
                this.updateIsDeletedControlStates(formArray);
            }),
            map(() => void 0),
        );
    }

    /**
     * Increments the weight of all order line item forms in the provided form array by 1.
     * This is typically used when adding a new order line item to ensure that the weights remain unique and sequential.
     *
     * @param formArray The FormArray containing the order line item forms.
     */
    incrementWeightForAllOrderLineItems(
        formArray: FormArray<FormGroup<FormGroupType<OrderLineItemFormModel>>>,
    ): void {
        formArray.controls.forEach(
            (form: FormGroup<FormGroupType<OrderLineItemFormModel>>) => {
                const currentWeight = form.controls.weight.value ?? 0;
                form.controls.weight.setValue(currentWeight + 1, {
                    emitEvent: false,
                });
            },
        );
    }

    /**
     * Updates the enabled/disabled state of the is deleted form controls in the form array
     * to ensure that at least one order line item remains undeleted.
     *
     * @param formArray The FormArray containing the order line item forms.
     */
    updateIsDeletedControlStates(
        formArray: FormArray<FormGroup<FormGroupType<OrderLineItemFormModel>>>,
    ): void {
        const nonMarkedForDeletionForms: FormGroup<
            FormGroupType<OrderLineItemFormModel>
        >[] = formArray.controls.filter(
            (form: FormGroup<FormGroupType<OrderLineItemFormModel>>) =>
                form.value.isDeleted !== true,
        );

        if (nonMarkedForDeletionForms.length === 1) {
            nonMarkedForDeletionForms[0].controls.isDeleted.disable({
                emitEvent: false,
            });
        } else {
            nonMarkedForDeletionForms.forEach(
                (form: FormGroup<FormGroupType<OrderLineItemFormModel>>) => {
                    if (form.controls.isDeleted.disabled) {
                        form.controls.isDeleted.enable({ emitEvent: false });
                    }
                },
            );
        }
    }

    /**
     * Patches the form array with order line items from the API response.
     * Converts numeric quantity and price values to strings for form compatibility.
     *
     * @param form The FormArray to be patched with order line item forms.
     * @param response The array of OrderLineItemResponse objects from the API.
     */
    patchFormArrayValue(
        form: FormArray<FormGroup<FormGroupType<OrderLineItemFormModel>>>,
        response: OrderLineItemResponse[],
    ): void {
        response.forEach((orderLineItemResponse) => {
            const orderLineItemForm = this.getFormDefinition();

            orderLineItemForm.patchValue({
                ...orderLineItemResponse,
                quantity: orderLineItemResponse.quantity.toString(),
                price: orderLineItemResponse.price.toString(),
            });

            form.push(orderLineItemForm);
        });
    }

    /**
     * Adds a new order line item form to the provided form array.
     *
     * @param form The FormArray to which the new order line item form will be added.
     * @param orderLineItemForm The FormGroup representing the new order line item.
     */
    addOrderLineItemForm(
        form: FormArray<FormGroup<FormGroupType<OrderLineItemFormModel>>>,
        orderLineItemForm: FormGroup<FormGroupType<OrderLineItemFormModel>>,
    ): void {
        form.push(orderLineItemForm);
    }

    generateRequestObject(
        form: FormArray<FormGroup<FormGroupType<OrderLineItemFormModel>>>,
    ): OrderLineItemRequest[] {
        return form.controls.map((orderLineItemFormGroup) => {
            const formValue: OrderLineItemFormModel =
                orderLineItemFormGroup.value as OrderLineItemFormModel;

            return {
                id: formValue.id,
                productId: formValue.productId,
                quantity: parseMaskedStringToNumber(formValue.quantity),
                price: parseMaskedStringToNumber(formValue.price),
                description: formValue.description,
                weight: formValue.weight,
                isDeleted: formValue.isDeleted,
            };
        });
    }
}
